import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Route } from '../routes';
import { useAuthService } from '../services/auth-service';
import IUser from '../shared/IUser';
import { getErrorMessages } from '../utils/error-utils';
import { storage } from '../utils/localstorage-utils';
import { auth as firebaseAuth } from '../firebase';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from 'firebase/auth';

type AuthContextObj = {
  isAuth: boolean;
  isLoggingIn: boolean;
  user: IUser | null;
  errors: string[] | null;
  login: (auth: any) => Promise<void>; 
  loginWithGoogle: () => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextObj>({
  isAuth: false,
  user: null,
  isLoggingIn: false,
  errors: null,
  login: async () => {},
  loginWithGoogle: async () => {},
  loginWithMicrosoft: async () => {},
  logout: async () => {},
});

export const useAuthContext = () => useContext(AuthContext);

const AuthContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  
  // Pulling the logic from your service hook
  const { getAuthUser, authenticate, authenticateFirebase, logoutUser } = useAuthService();

  const [user, setUser] = useState<IUser | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(true); // Default to true while checking session
  const [errors, setErrors] = useState<string[] | null>(null);
  const queryClient = useQueryClient();

  const logout = useCallback(async () => {
    try {
      if (logoutUser) {
        await logoutUser();
      }
    } catch (e) {
      console.error("Logout failed:", e);
    }
    storage.deleteAuthentication();
    setUser(null);
    queryClient.clear();
    navigate(Route.auth);
  }, [navigate, logoutUser, queryClient]);

  const tryAutoLogin = useCallback(async () => {
    setIsLoggingIn(true);
    try {
      const userData = await getAuthUser();
      if (userData) {
        setUser(userData);
      }
    } catch (e) {
      console.log("Auto-login failed:", e);
    } finally {
      setIsLoggingIn(false);
    }
  }, [getAuthUser]);

  const login = useCallback(async (authCredentials: any) => {
    setIsLoggingIn(true);
    setErrors(null);
    try {
      const response = await authenticate(authCredentials);
      if (response.status === 'error' || response.status === 'ERROR') {
        throw new Error(response.message || 'Invalid username or password.');
      }
      if (response.user) {
        setUser(response.user);
        queryClient.clear();
        if (response.accessToken) {
          storage.saveAuthentication(response.accessToken, response.user.username || response.user.email);
        }
      } else if (response.accessToken) {
        throw new Error("Login successful, but user data is missing.");
      } else {
        throw new Error("Unexpected login response.");
      }

      if (response.user?.role === 'SUPERADMIN') {
        navigate(Route.settings);
      } else {
        navigate(Route.reserve); 
      }

    } catch (e) {
      setErrors(getErrorMessages(e));
    } finally {
      setIsLoggingIn(false);
    }
  }, [authenticate, navigate, queryClient]);

  const loginWithFirebase = useCallback(async (provider: GoogleAuthProvider | OAuthProvider) => {
    setIsLoggingIn(true);
    setErrors(null);
    try {
      const result = await signInWithPopup(firebaseAuth, provider);
      const idToken = await result.user.getIdToken();
      
      const response = await authenticateFirebase(idToken);
      if (response.user) {
        setUser(response.user);
        queryClient.clear();
        if (response.accessToken) {
          storage.saveAuthentication(response.accessToken, response.user.username || response.user.email, idToken);
        }
      } else if (response.accessToken) {
        throw new Error("Firebase login successful, but user data is missing.");
      } else {
        throw new Error("Unexpected Firebase login response.");
      }

      if (response.user?.role === 'SUPERADMIN') {
        navigate(Route.settings);
      } else {
        navigate(Route.reserve);
      }
    } catch (e: any) {
      console.error("Firebase login error:", e);
      setErrors(getErrorMessages(e));
    } finally {
      setIsLoggingIn(false);
    }
  }, [authenticateFirebase, navigate, queryClient]);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    await loginWithFirebase(provider);
  }, [loginWithFirebase]);

  const loginWithMicrosoft = useCallback(async () => {
    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      tenant: 'common',
    });
    await loginWithFirebase(provider);
  }, [loginWithFirebase]);

  useEffect(() => {
    tryAutoLogin();
  }, [tryAutoLogin]);

  return (
    <AuthContext.Provider
      value={{
        isAuth: !!user,
        isLoggingIn,
        user,
        errors,
        login,
        loginWithGoogle,
        loginWithMicrosoft,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;